import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink } from "lucide-react";

const PLATFORM_COLORS = {
  telegram: "#229ED9",
  twitter: "#1DA1F2",
  instagram: "#E1306C",
  youtube: "#FF0000",
  any: "#2ecc71",
};

export default function Tasks() {
  const { refreshUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [handle, setHandle] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/social/tasks");
    setTasks(data.tasks || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const verify = async (t) => {
    setBusy(t.task_id);
    try {
      const { data } = await api.post(`/social/verify/${t.task_id}`, {
        handle: handle[t.task_id] || "",
      });
      toast.success(`+${data.bc_earned} BC — ${data.note}`);
      await refreshUser();
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Layout>
      <div className="px-4 py-5 flex flex-col gap-4" data-testid="tasks-page">
        <h1 className="font-pixel text-[14px] text-glow-green">◼ TASKS</h1>
        <p className="text-[11px] text-[#8b95a9] -mt-2">
          Complete social actions to earn BOOM Credits. Some tasks verify via
          real APIs when configured.
        </p>

        {loading && (
          <div className="text-[11px] text-[#8b95a9]">Loading...</div>
        )}

        {tasks.map((t) => {
          const needsHandle =
            ["twitter_api", "telegram_api"].includes(t.verification_method);
          return (
            <div
              key={t.task_id}
              className="cb-card p-4 flex flex-col gap-3"
              data-testid={`task-${t.task_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="cb-pill text-[8px]"
                      style={{
                        color: PLATFORM_COLORS[t.platform] || "#2ecc71",
                        borderColor:
                          (PLATFORM_COLORS[t.platform] || "#2ecc71") + "66",
                      }}
                    >
                      {t.platform.toUpperCase()}
                    </span>
                    <span className="text-[10px] text-[#f7931a] font-pixel">
                      +{t.reward} BC
                    </span>
                  </div>
                  <div className="text-[12px] text-white mt-2">{t.name}</div>
                  {t.completed_count > 0 && (
                    <div className="text-[9px] text-[#2ecc71] mt-1">
                      <CheckCircle2 size={9} className="inline mr-1" />
                      Completed {t.completed_count}x
                    </div>
                  )}
                </div>
                {t.url && (
                  <a
                    href={t.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`task-visit-${t.task_id}`}
                    className="text-[#2e75b6] shrink-0"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
              {needsHandle && (
                <input
                  className="cb-input"
                  placeholder={
                    t.verification_method === "twitter_api"
                      ? "your x/twitter handle"
                      : "your telegram user id"
                  }
                  value={handle[t.task_id] || ""}
                  onChange={(e) =>
                    setHandle({ ...handle, [t.task_id]: e.target.value })
                  }
                  data-testid={`task-handle-${t.task_id}`}
                />
              )}
              <button
                disabled={!t.can_complete || busy === t.task_id}
                onClick={() => verify(t)}
                data-testid={`task-verify-${t.task_id}`}
                className="cb-neon-btn"
              >
                {taskButtonLabel(t, busy === t.task_id)}
              </button>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}

function taskButtonLabel(task, isBusy) {
  if (isBusy) return "verifying...";
  if (!task.can_complete) return "COMPLETED";
  return "Verify & Claim";
}
